if (!customElements.get('pack-builder')) {
  customElements.define(
    'pack-builder',
    class PackBuilder extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(this.querySelector('[data-pack-variants]').textContent).map((v) => ({
          ...v,
          counts: PackBuilder.parse(v.title),
        }));
        this.items = Array.from(this.querySelectorAll('[data-label]'));
        this.labels = this.items.map((li) => li.dataset.label);
        this.size = Math.max(...this.variants.map((v) => PackBuilder.total(v.counts)));
        this.counts = Object.fromEntries(this.labels.map((l) => [l, 0]));

        // Si la URL trae ?variant=, se parte con esa combinación armada
        const selected = this.variants.find((v) => String(v.id) === this.dataset.selected);
        if (selected) this.labels.forEach((l) => (this.counts[l] = selected.counts[l] || 0));

        this.items.forEach((li) => {
          const label = li.dataset.label;
          li.querySelector('[data-pack-plus]').addEventListener('click', () => this.change(label, 1));
          li.querySelector('[data-pack-minus]').addEventListener('click', () => this.change(label, -1));
        });

        // Nada se agrega al carrito con el pack a medias: ni el botón, ni la barra fija
        this.form = document.getElementById(this.dataset.formId);
        this.form?.addEventListener(
          'submit',
          (event) => {
            if (this.variant) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.nudge();
          },
          true
        );

        this.renderRules();
        Promise.all(['product-form', 'product-info'].map((t) => customElements.whenDefined(t))).then(() => this.update());
      }

      /** "2 Lavado + 1 Honey" → {Lavado: 2, Honey: 1} */
      static parse(title) {
        return Object.fromEntries(
          title.split('+').map((part) => {
            const m = part.trim().match(/^(\d+)\s+(.+)$/);
            return m ? [m[2].trim(), Number(m[1])] : [part.trim(), 1];
          })
        );
      }

      static total(counts) {
        return Object.values(counts).reduce((a, b) => a + b, 0);
      }

      /** ¿Se puede llegar a alguna combinación con stock partiendo de estos conteos? */
      reachable(counts) {
        return this.variants.some(
          (v) => v.available && this.labels.every((l) => (counts[l] || 0) <= (v.counts[l] || 0))
        );
      }

      change(label, delta) {
        const next = { ...this.counts, [label]: this.counts[label] + delta };
        if (next[label] < 0 || PackBuilder.total(next) > this.size) return;
        if (delta > 0 && !this.reachable(next)) return;
        this.counts = next;
        this.update();
      }

      get variant() {
        if (PackBuilder.total(this.counts) !== this.size) return null;
        return (
          this.variants.find((v) => this.labels.every((l) => (v.counts[l] || 0) === this.counts[l])) || null
        );
      }

      update() {
        const total = PackBuilder.total(this.counts);
        const missing = this.size - total;

        this.items.forEach((li) => {
          const label = li.dataset.label;
          const plus = li.querySelector('[data-pack-plus]');
          const canAdd = missing > 0 && this.reachable({ ...this.counts, [label]: this.counts[label] + 1 });
          plus.disabled = !canAdd;
          li.querySelector('[data-pack-minus]').disabled = this.counts[label] === 0;
          li.querySelector('[data-pack-count]').textContent = this.counts[label];
          li.classList.toggle('is-active', this.counts[label] > 0);

          const inStock = this.variants.some((v) => v.available && v.counts[label]);
          li.querySelector('[data-pack-note]').textContent = !inStock
            ? window.variantStrings.soldOut
            : missing > 0 && !canAdd
            ? 'No combina con lo que elegiste'
            : '';
        });

        this.querySelector('[data-pack-progress]').textContent = `${total} de ${this.size}`;
        this.renderBox();

        const info = this.closest('product-info');
        const productForm = info?.querySelector('product-form');
        const variant = this.variant;
        info?.classList.toggle('pack-incomplete', !variant);

        if (variant) {
          info?.updateVariantInputs?.(variant.id);
          info?.updateURL?.(info.dataset.url, variant.id);
          productForm?.toggleSubmitButton(!variant.available, window.variantStrings.soldOut);
        } else {
          info?.updateURL?.(info.dataset.url, null);
          const text =
            missing === 1 ? this.dataset.missingOne : this.dataset.missingMany.replace('[n]', missing);
          productForm?.toggleSubmitButton(true, text);
        }
      }

      renderBox() {
        const box = this.querySelector('[data-pack-box]');
        // Solo se anima el casillero que se acaba de llenar, no todos
        const total = PackBuilder.total(this.counts);
        const added = total > (this.lastTotal ?? total) ? total - 1 : -1;
        this.lastTotal = total;
        const slots = [];
        this.labels.forEach((label, i) => {
          for (let n = 0; n < this.counts[label]; n++) slots.push(i);
        });
        box.replaceChildren(
          ...Array.from({ length: this.size }, (_, n) => {
            const slot = document.createElement('span');
            slot.className = 'pack-builder__slot';
            if (n < slots.length) {
              const li = this.items[slots[n]];
              slot.classList.add('is-filled');
              if (n === added) slot.classList.add('is-new');
              slot.append(li.querySelector('.pack-builder__media').cloneNode(true));
              const name = document.createElement('span');
              name.className = 'pack-builder__slot-name';
              name.textContent = li.dataset.label;
              slot.append(name);
            }
            return slot;
          })
        );
      }

      /**
       * Mínimos que se deducen de las combinaciones permitidas: si ninguna
       * combinación viene sin Lavado → "Cada pack debe incluir al menos: 1 Lavado."
       */
      renderRules() {
        const mins = this.labels
          .map((l) => [l, Math.min(...this.variants.map((v) => v.counts[l] || 0))])
          .filter(([, min]) => min > 0)
          .map(([l, min]) => `${min} ${l}`);
        const list = mins.length > 1 ? `${mins.slice(0, -1).join(', ')} y ${mins.at(-1)}` : mins[0];
        this.querySelector('[data-pack-rules]').textContent = list ? `Cada pack debe incluir al menos: ${list}.` : '';
      }

      nudge() {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.classList.remove('is-nudged');
        void this.offsetWidth;
        this.classList.add('is-nudged');
      }
    }
  );
}
